/**
 * CAPTCHA challenge for the public form page.
 *
 * The backend has verified CAPTCHA tokens since the public-form spam gate
 * shipped, and `public_form_tokens.captcha_required` has been settable all
 * along — but nothing ever RENDERED a challenge, so no submission carried a
 * `captcha_token`. That left two failure modes, both silent:
 *
 *   secret set, no widget    every submission rejected as "captcha_missing".
 *                            The form is dead and the submitter sees only a
 *                            generic failure.
 *   secret unset, no widget  verification fails OPEN — the submission is
 *                            accepted and merely flagged. The CAPTCHA is
 *                            decorative, and the org believes it is protected.
 *
 * So a form cannot safely be marked captcha_required until this component is
 * on the page. It renders the provider's widget, hands the resulting token to
 * its parent, and clears it when the challenge expires so a stale token is
 * never submitted.
 *
 * Provider and site key both come from the server (`GET /api/public/forms/
 * {token}`), never from a build-time env var — the public form is served to
 * anonymous browsers and the site key is per-deployment.
 */
import React, { useEffect, useRef } from 'react';

// The three providers the backend's verify_captcha knows how to check. Their
// browser APIs are near-identical, which is why one component covers all of
// them; only the script URL and the global differ.
const PROVIDER_SCRIPTS: Record<string, { src: string; global: string }> = {
  turnstile: {
    src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
    global: 'turnstile',
  },
  hcaptcha: {
    src: 'https://js.hcaptcha.com/1/api.js?render=explicit',
    global: 'hcaptcha',
  },
  recaptcha: {
    src: 'https://www.google.com/recaptcha/api.js?render=explicit',
    global: 'grecaptcha',
  },
};

type Props = {
  provider?: string | null;
  siteKey?: string | null;
  /** Called with the challenge token, or null when it expires or errors. */
  onToken: (token: string | null) => void;
  /** Language to render the challenge in, matching the form's own language. */
  language?: string;
};

/** Load a provider script once, even if two widgets mount in the same page. */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      // Already resolved earlier in this page's life.
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('captcha script failed')));
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.defer = true;
    el.addEventListener('load', () => {
      el.dataset.loaded = 'true';
      resolve();
    });
    el.addEventListener('error', () => reject(new Error('captcha script failed')));
    document.head.appendChild(el);
  });
}

export const CaptchaWidget: React.FC<Props> = ({ provider, siteKey, onToken, language }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | number | null>(null);
  // onToken is called from provider callbacks that are registered once; holding
  // it in a ref keeps those callbacks current without re-rendering the widget,
  // which would reset a challenge the submitter has already solved.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const key = (provider || 'turnstile').toLowerCase();
  const config = PROVIDER_SCRIPTS[key];

  useEffect(() => {
    if (!siteKey || !config || !containerRef.current) return;
    let cancelled = false;

    loadScript(config.src)
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const api = (window as any)[config.global];
        if (!api?.render) return;

        // grecaptcha defers readiness behind .ready(); the other two are usable
        // as soon as the script has run.
        const render = () => {
          if (cancelled || !containerRef.current) return;
          // Guard against a double-render in React StrictMode, which would
          // otherwise stack two challenges in the same container.
          if (widgetIdRef.current !== null) return;
          try {
            widgetIdRef.current = api.render(containerRef.current, {
              sitekey: siteKey,
              language,
              callback: (tok: string) => onTokenRef.current(tok),
              'expired-callback': () => onTokenRef.current(null),
              'error-callback': () => onTokenRef.current(null),
            });
          } catch {
            // A malformed site key throws here. Leave the token null: the
            // submitter sees the form's own "please complete the check"
            // message rather than a broken widget with no explanation.
            onTokenRef.current(null);
          }
        };

        if (key === 'recaptcha' && typeof api.ready === 'function') api.ready(render);
        else render();
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      const api = (window as any)[config?.global || ''];
      if (widgetIdRef.current !== null && api?.remove) {
        try {
          api.remove(widgetIdRef.current);
        } catch {
          /* provider already tore it down */
        }
      }
      widgetIdRef.current = null;
    };
    // Re-rendering on language change would discard a solved challenge, so the
    // widget is built once per site key and provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, key]);

  if (!siteKey || !config) return null;

  return <div ref={containerRef} className="my-4" data-testid="captcha-widget" />;
};

export default CaptchaWidget;
